import accountService from '../services/account.service.js'
import authService from '../services/auth.service.js'
import userService from '../services/user.service.js'

import { useAuthStore } from '../stores/authStore.js'

import httpUtility from '../utils/http.utility.js'

import { useToast } from '../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

/** Keep request headers aligned with DAL session during account switch. */
function patchSessionContext({ account, groupMode }) {
  const authStore = useAuthStore()
  const user = authStore.user
  if (!user) return

  const accountId = account?.id ?? account?._id
  authStore.hydrate.user({
    ...user,
    groupMode: groupMode ?? false,
    currentAccount: accountId
      ? {
          ...user.currentAccount,
          _id: accountId,
          name: account.name ?? user.currentAccount?.name,
        }
      : user.currentAccount,
  })
}

async function getCurrentUser() {
  try {
    return (await userService.get.current()) ?? null
  } catch {
    return null
  }
}

async function updateAccount(accountId) {
  const authStore = useAuthStore()
  const previousUser = authStore.user

  try {
    const switchRes = await accountService.update.switch(accountId)
    // Align request headers with DAL/Mongo before get.current — otherwise
    // protect returns CONTEXT_MISMATCH against the account we just switched to.
    patchSessionContext({
      account: switchRes.data?.account,
      groupMode: switchRes.data?.groupMode,
    })
    const current = await userService.get.current()
    if (!current) {
      if (previousUser) authStore.hydrate.user(previousUser)
      toast.error(t('common.auth.errors.switchAccount'))
      return null
    }
    return current
  } catch (err) {
    if (previousUser) authStore.hydrate.user(previousUser)
    toast.error(httpUtility.error.message(err, t('common.auth.errors.switchAccount')))
    return null
  }
}

async function updateGroupmode() {
  try {
    const groupMode = await userService.update.groupmode()
    const authStore = useAuthStore()
    if (authStore.user && groupMode != null) {
      authStore.hydrate.user({ ...authStore.user, groupMode })
    }
    return groupMode
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('common.auth.errors.groupMode')))
    return null
  }
}

async function login(email, password) {
  try {
    await authService.login(email, password)
    const current = await userService.get.current()
    if (!current) throw new Error('No user')
    return current
  } catch (err) {
    throw new Error(httpUtility.error.message(err, t('common.auth.errors.login')), { cause: err })
  }
}

async function logout() {
  try {
    await authService.logout()
  } catch (err) {
    console.error('Logout error:', err)
  }
}

class AuthController {
  get = {
    /**
     * Load the authenticated user. Returns the user on success, `null` on
     * failure (no toast).
     *
     * @returns {Promise<object|null>}
     */
    async current() {
      return getCurrentUser()
    },
  }

  update = {
    /**
     * Switch to a different account and return the refreshed user.
     * Returns the user on success, `null` on failure (toasts the error).
     *
     * @param {string} accountId
     * @returns {Promise<object|null>}
     */
    async account(accountId) {
      return updateAccount(accountId)
    },

    /**
     * Toggle the group-mode flag. Returns the new boolean on success,
     * `null` on failure (toasts the error).
     *
     * @returns {Promise<boolean|null>}
     */
    async groupmode() {
      return updateGroupmode()
    },
  }

  /**
   * Authenticate and return the current user. Throws a translated
   * `Error` on failure (with the original error as `cause`).
   *
   * @param {{ email: string, password: string }} credentials
   * @returns {Promise<object>}
   */
  async login(credentials) {
    return login(credentials.email, credentials.password)
  }

  /**
   * Invalidate the DAL session. Always resolves; network errors are
   * logged only.
   *
   * @returns {Promise<void>}
   */
  async logout() {
    return logout()
  }
}

export default new AuthController()
